import type { Game } from '../../game/Game';

/**
 * Plays hero clips on the local player's ActorView without touching the sim
 * (no fake casts). Looping clips are kept alive by re-triggering them as
 * one-shots each cycle, because the view falls back to Idle/Run as soon as a
 * one-shot ends.
 */

/** Clip lengths (seconds) of hero clips built by tools/blender (30 fps). */
export const CLIP_SECONDS: Record<string, number> = {
  Gather: 1.0,
  Fish: 2.0,
  Channel: 1.33,
  Emote_Dance: 1.6,
  Emote_Sit: 2.0,
  Emote_Wave: 1.33,
  Emote_Cheer: 1.33,
  Emote_Bow: 1.4,
  Victory: 1.6,
  Buff: 1.27,
};

/** Clips the local player's current model provides (null when unknown). */
export function playerClips(game: Game): Set<string> | null {
  const p = game.player;
  if (!p) return null;
  const id = game.assets.resolve(p.look.model);
  const clips = id ? game.assets.entry(id)?.clips : undefined;
  return clips ? new Set(clips) : null;
}

/** First clip of `chain` the model has (or the first entry when the clip list is unknown). */
export function pickClip(chain: readonly string[], clips: Set<string> | null): string | null {
  if (!clips) return chain[0] ?? null;
  return chain.find((c) => clips.has(c)) ?? null;
}

/** Plays a clip once on the local player. */
export function playOnce(game: Game, chain: readonly string[]): string | null {
  const p = game.player;
  const v = p ? game.zone?.views.get(p.id) : undefined;
  const clip = pickClip(chain, playerClips(game));
  if (v && clip) v.play(clip, 0.12);
  return clip;
}

/** Keeps a looping clip playing on the local player until stopped. */
export class PlayerClipLoop {
  clip: string | null = null;
  private period = 1;
  private t = 0;

  get active(): boolean {
    return this.clip !== null;
  }

  start(game: Game, chain: readonly string[]): void {
    this.clip = pickClip(chain, playerClips(game));
    this.period = (this.clip && CLIP_SECONDS[this.clip]) || 1.2;
    this.t = 0;
    this.update(game, 0);
  }

  /** Stops the loop; `release` also ends the current one-shot so the view returns to Idle/Run at once. */
  stop(game: Game, release = true): void {
    if (!this.clip) return;
    this.clip = null;
    const p = game.player;
    // Never cut a skill animation that has just taken over.
    if (release && p && !p.cast) game.zone?.views.get(p.id)?.endStage();
  }

  update(game: Game, dt: number): void {
    if (!this.clip) return;
    this.t -= dt;
    if (this.t > 0) return;
    const p = game.player;
    const v = p ? game.zone?.views.get(p.id) : undefined;
    if (!v) return;
    // Re-trigger a frame early so the view never blends back to Idle between cycles.
    v.play(this.clip, 0.1, this.period, false);
    this.t = this.period - 1 / 30;
  }
}
